'use client';

import { FC, useContext, useState, useMemo } from 'react';
import { NotificationsProps } from './Notifications.types';
import {
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Popover,
  Separator,
  Tabs,
  TabsItem,
} from '@/_components';
import type { Notification } from '@/_types/models/notifications';
import { NotificationsContext } from '@/_contexts/admin/NotificationsProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { NotificationsList } from './components/NotificationsList';
import styles from './Notifications.module.css';
import { getClasses } from '@/_utils/classnames';
import Link from 'next/link';
import { ToastContext } from '@/_contexts/ToastProvider';
import {
  useDownloadNotification,
  isDownloadLink,
} from '@/_utils/notifications';

export const Notifications: FC<NotificationsProps> = () => {
  const {
    dismissAll,
    isLoading,
    isOpen,
    notifications,
    reloadNotifications,
    setOpen,
    unreadCount,
  } = useContext(NotificationsContext);
  const { isAdvisor } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const downloadNotification = useDownloadNotification();

  const [activeTab, setActiveTab] = useState<0 | 1>(0);
  const [isDownloading, setIsDownloading] = useState(false);

  const hasDownloadableNotifications: boolean = useMemo(
    () =>
      notifications?.some(
        (notification) =>
          !notification.dismissed &&
          notification.link &&
          isDownloadLink(notification.link),
      ),
    [notifications],
  );

  if (!isAdvisor) return null;

  window.addEventListener('message', (event) => {
    if (event.data?.type === 'vue-app-iframe-click') {
      setOpen(false);
    }
  });

  const tabItems: Array<TabsItem> = [
    { title: 'All', isActive: activeTab === 0, onClick: () => setActiveTab(0) },
    {
      title: 'New',
      isActive: activeTab === 1,
      onClick: () => setActiveTab(1),
      flair: unreadCount,
    },
  ];

  const newNotifications: Array<Notification> = notifications?.filter(
    (n) => !n.dismissed,
  );

  const selectedNotifications: Array<Notification> =
    activeTab === 0 ? notifications : newNotifications;

  const notificationsClasses: string = getClasses(
    styles.notifications,
    'elevation-xl',
  );

  const downloadAll = async () => {
    setIsDownloading(true);
    let failedCount = 0;

    try {
      const undismissedDownloadableNotifications = notifications.filter(
        (notification) =>
          !notification.dismissed &&
          notification.link &&
          isDownloadLink(notification.link),
      );

      // Process downloads sequentially to avoid browser download limitations
      for (const notification of undismissedDownloadableNotifications) {
        try {
          const success = await downloadNotification(notification);
          if (!success) {
            failedCount++;
          }
        } catch (error) {
          console.error('Error downloading notification:', error);
          failedCount++;
        }
      }

      if (failedCount <= 0) {
        addToast('success', 'All documents downloaded successfully');
      } else {
        addToast('error', `Failed to download ${failedCount} document(s)`);
      }
    } catch (error) {
      addToast(
        'error',
        'An unexpected error occurred while downloading documents',
        { error },
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Popover placement="bottom" open={isOpen} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Button
          intent="ghost"
          isLoading={isLoading}
          iconLeft="bell"
          value="Notifications"
          flair={unreadCount}
          isIconOnly={true}
        />
      </Popover.Trigger>
      <Popover.Content padding={0} maxHeight="500px">
        <Flex isColumn={true} className={notificationsClasses}>
          <Flex
            isBetween={true}
            isYCentered={true}
            className={styles.notifications__header}
          >
            <Heading level={5}>Notifications</Heading>
          </Flex>
          <Separator />
          <Tabs
            items={tabItems}
            isSticky={true}
            tabActions={
              <ButtonGroup isSmallGroup={true} isSameLine={true}>
                <Button
                  onClick={reloadNotifications}
                  intent="ghost"
                  size="tiny"
                  value="Refresh"
                  isIconOnly={true}
                  withTooltip={true}
                  iconLeft="refresh"
                />
                {hasDownloadableNotifications && (
                  <Button
                    intent="ghost"
                    size="tiny"
                    iconLeft="download"
                    isIconOnly={true}
                    value="Download all"
                    withTooltip={true}
                    onClick={downloadAll}
                    isLoading={isDownloading}
                  />
                )}
                <Button
                  intent="ghost"
                  size="tiny"
                  isDisabled={!unreadCount}
                  withTooltip={true}
                  isIconOnly={true}
                  iconLeft="eye"
                  value="Mark all as read"
                  onClick={dismissAll}
                />
                <Link href="/notifications">
                  <Button
                    intent="ghost"
                    size="tiny"
                    iconRight="arrow-right"
                    value="View all"
                    onClick={() => setOpen(false)}
                  />
                </Link>
              </ButtonGroup>
            }
          >
            <NotificationsList notifications={selectedNotifications} />
          </Tabs>
        </Flex>
      </Popover.Content>
    </Popover>
  );
};
